import React, { Component } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
// components
import { FormattedMessage } from 'react-intl';
import Button, { TransparentBtn } from 'components/Button';
import Panel from 'components/Panel';
import FormattedWeight from 'components/FormattedWeight';
import DataTable from 'components/DataTable/DataTable';
import NothingBox from 'components/NothingBox';
import Preloader from 'components/Preloader';
import { CustomColumn } from 'components/DataTable/Columns';
// api
import { getTemperaturePoints, getDaysOnFeedTemperaturePoints } from 'endpoints/common';
import { updateAllTemperaturePoints } from 'endpoints/admin/temperaturePoints';
import {
  updateAllDaysOnFeedTemperaturePoints,
} from 'endpoints/admin/daysOnFeedTemperaturePoints';
// utils
import { isMobile } from 'react-device-detect';
import { formatBackEndWeightToSystem } from 'utils/weightHelper';
import { calcSetPointValue, formatSetpointValue, showToastrMessage } from 'utils';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
// styles
import './SetPoints.scss';

const tableFarmTypes = ['nursery', 'wf', 'finisher'];

const tableFarmTypesLabels = {
  nursery: 'n_value',
  wf: 'wf_value',
  finisher: 'f_value',
};

const DATASET_TYPES = {
  WEIGHT_BASED: 'weight_based',
  DAYS_ON_FEED: 'days_on_feed'
};

class SetPoints extends Component {

  constructor(props) {
    super(props);

    const { temperatureSetpointSource } = props;
    const currentSource = temperatureSetpointSource || 'weight_based_source';
    const activeDataset = currentSource === 'days_on_feed_source'
      ? DATASET_TYPES.DAYS_ON_FEED
      : DATASET_TYPES.WEIGHT_BASED;

    this.state = {
      isEditMode: false,
      activeDataset, // Which dataset is currently being edited

      // Weight-based data
      weightBasedData: [],
      weightBasedDataToSave: [],
      weightBasedLoading: false,

      // Days-on-feed data
      daysOnFeedData: [],
      daysOnFeedDataToSave: [],
      daysOnFeedLoading: false,

      // Current setpoint source setting
      currentSource,
    };
  }

  componentDidMount() {
    this.fetchAllData();
  }

  componentDidUpdate(prevProps) {
    // Update activeDataset and currentSource when temperatureSetpointSource prop changes
    const { temperatureSetpointSource } = this.props;
    if (prevProps.temperatureSetpointSource !== temperatureSetpointSource) {
      const currentSource = temperatureSetpointSource || 'weight_based_source';
      const activeDataset = currentSource === 'days_on_feed_source'
        ? DATASET_TYPES.DAYS_ON_FEED
        : DATASET_TYPES.WEIGHT_BASED;

      this.setState({
        currentSource,
        activeDataset,
      });
    }
  }

  fetchAllData = () => {
    Promise.all([
      this.fetchWeightBasedPoints(),
      this.fetchDaysOnFeedPoints()
    ]);
  };

  fetchWeightBasedPoints = () => {
    this.setState({ weightBasedLoading: true });
    return getTemperaturePoints().then((response) => {
      const resources = response.resources || response || [];
      const dataArray = Array.isArray(resources) ? resources : [];

      this.setState({
        weightBasedLoading: false,
        weightBasedData: dataArray,
        weightBasedDataToSave: dataArray,
      });
    }).catch((errors) => {
      this.setState({
        weightBasedLoading: false,
        weightBasedData: [],
        weightBasedDataToSave: []
      });
      return toastResponseErrors(errors);
    });
  };

  fetchDaysOnFeedPoints = () => {
    this.setState({ daysOnFeedLoading: true });
    return getDaysOnFeedTemperaturePoints().then((response) => {
      const resources = response.resources || [];
      const dataArray = Array.isArray(resources) ? resources : [];

      // Only update the state data, don't change activeDataset/currentSource as they're set from props
      this.setState({
        daysOnFeedLoading: false,
        daysOnFeedData: dataArray,
        daysOnFeedDataToSave: dataArray,
      });
    }).catch((errors) => {
      this.setState({
        daysOnFeedLoading: false,
        daysOnFeedData: [],
        daysOnFeedDataToSave: [],
      });
      return toastResponseErrors(errors);
    });
  };

  updateWeightBasedPoints = () => {
    const { weightBasedDataToSave } = this.state;
    this.setState({ weightBasedLoading: true });

    updateAllTemperaturePoints({ temperature_points: weightBasedDataToSave })
      .then((response) => {
        const resources = response.resources || response || [];
        const dataArray = Array.isArray(resources) ? resources : [];

        this.setState({
          weightBasedLoading: false,
          weightBasedData: dataArray,
          weightBasedDataToSave: dataArray,
          isEditMode: false,
        });
        showToastrMessage('component.toastr.setPoints.successUpdated');
      })
      .catch((errors) => {
        this.setState({ weightBasedLoading: false });
        return toastResponseErrors(errors);
      });
  };

  updateDaysOnFeedPoints = () => {
    const { daysOnFeedDataToSave } = this.state;
    this.setState({ daysOnFeedLoading: true });

    updateAllDaysOnFeedTemperaturePoints({ temperature_points: daysOnFeedDataToSave })
      .then((response) => {
        const resources = response.resources || [];
        const dataArray = Array.isArray(resources) ? resources : [];

        this.setState({
          daysOnFeedLoading: false,
          daysOnFeedData: dataArray,
          daysOnFeedDataToSave: dataArray,
          isEditMode: false,
        });
        showToastrMessage('component.toastr.setPoints.successUpdated');
      })
      .catch((errors) => {
        this.setState({ daysOnFeedLoading: false });
        return toastResponseErrors(errors);
      });
  };

  renderFirstColumn = (column, isWeightBased = true) => {
    const { estimated_weight, days_on_feed, rule_type } = column;
    const { measurementSystem } = this.props;

    if (isWeightBased) {
      const value = formatBackEndWeightToSystem(estimated_weight, measurementSystem);
      return (
        <CustomColumn textCenter className="weight-column">
          <FormattedWeight className="semibold" value={value} afterValue={rule_type === 'gteq' && '+'} />
        </CustomColumn>
      );
    }
    return (
      <CustomColumn textCenter className="weight-column">
        <span className="semibold">
          {days_on_feed || 0}{rule_type === 'gteq' && '+'}
        </span>
      </CustomColumn>
    );
  };

  renderFarmTypeColumn = (valueKey, isWeightBased = true) => (column) => {
    const { temperatureSystem } = this.props;
    const { isEditMode, activeDataset, weightBasedDataToSave, daysOnFeedDataToSave } = this.state;

    const isActiveDataset = (isWeightBased && activeDataset === DATASET_TYPES.WEIGHT_BASED) ||
                           (!isWeightBased && activeDataset === DATASET_TYPES.DAYS_ON_FEED);

    const dataToSave = isWeightBased ? weightBasedDataToSave : daysOnFeedDataToSave;
    const actualValue = (isEditMode && isActiveDataset)
      ? dataToSave.find(({ id }) => id === column.id)?.[valueKey]
      : column[valueKey];
    const value = calcSetPointValue(actualValue, temperatureSystem);

    // Both dataset types now support the same maximum value
    const maxValue = 10000;

    return (
      <CustomColumn textCenter>
        {(isEditMode && isActiveDataset)
          ? (
            <input
              type="number"
              min="0"
              max={maxValue}
              step="0.1"
              pattern="[0-9]+([\,|\.][0-9]+)?"
              className="farmtype-temp-input"
              value={value || ''}
              placeholder="--"
              onChange={this.onTempSetPointChange(column.id, valueKey, isWeightBased)}
            />
          )
          : formatSetpointValue(value)}
      </CustomColumn>
    );
  };

  onTempSetPointChange = (id, valueKey, isWeightBased = true) => ({ target: { value, validity } }) => {
    if (!validity.valid) return;

    const { temperatureSystem } = this.props;
    const dataToSaveKey = isWeightBased ? 'weightBasedDataToSave' : 'daysOnFeedDataToSave';
    const dataToSave = this.state[dataToSaveKey];

    const safeDataToSave = Array.isArray(dataToSave) ? dataToSave : [];
    const newValue = calcSetPointValue(Number(value), 'fahrenheit', temperatureSystem);

    // Validate against database constraints
    const maxValue = 10000; // Both dataset types now support the same maximum value
    const actualValue = Number(value);

    if (actualValue > maxValue) {
      showToastrMessage('component.toastr.setPoints.maxValueExceeded', 'error');
      return;
    }

    const column = safeDataToSave.find((col) => col.id === id);

    if (!column) return;

    const colIndex = safeDataToSave.indexOf(column);

    this.setState({
      [dataToSaveKey]: [
        ...safeDataToSave.slice(0, colIndex),
        { ...column, [valueKey]: newValue },
        ...safeDataToSave.slice(colIndex + 1),
      ],
    });
  };

  getFarmTypesColumns = (isWeightBased = true) => {
    const { temperatureSystem } = this.props;
    const value = <FormattedMessage id={`general.${temperatureSystem}`} />;
    return (
      tableFarmTypes.map((type) => (
        {
          label: <FormattedMessage id={`container.setPoints.tableHeaders.${type}`} values={{ value }} />,
          flex: '1 0 150px',
          renderer: this.renderFarmTypeColumn(tableFarmTypesLabels[type], isWeightBased),
          className: 'setpoint-table-cell'
        }
      ))
    );
  };

  switchEditMode = (datasetType) => {
    this.setState({
      isEditMode: true,
      activeDataset: datasetType
    });
  };

  resetToDefault = () => {
    const { activeDataset } = this.state;

    if (activeDataset === DATASET_TYPES.WEIGHT_BASED) {
      this.setState((prevState) => ({
        weightBasedDataToSave: [...prevState.weightBasedData],
        isEditMode: false
      }));
    } else {
      this.setState((prevState) => ({
        daysOnFeedDataToSave: [...prevState.daysOnFeedData],
        isEditMode: false
      }));
    }
    showToastrMessage('component.toastr.setPoints.successReseted');
  };

  saveChanges = () => {
    const { activeDataset } = this.state;

    if (activeDataset === DATASET_TYPES.WEIGHT_BASED) {
      this.updateWeightBasedPoints();
    } else {
      this.updateDaysOnFeedPoints();
    }
  };

  switchDataset = (datasetType) => {
    // Only update the active dataset for viewing purposes
    // Do NOT update the global temperature setpoint source setting
    this.setState({
      activeDataset: datasetType,
      isEditMode: false
    });
  };

  renderDatasetTable = (isWeightBased = true) => {
    const {
      isEditMode, activeDataset, weightBasedData, daysOnFeedData,
      weightBasedLoading, daysOnFeedLoading,
    } = this.state;

    const data = isWeightBased ? weightBasedData : daysOnFeedData;
    const loading = isWeightBased ? weightBasedLoading : daysOnFeedLoading;
    const safeData = Array.isArray(data) ? data : [];
    const datasetType = isWeightBased ? DATASET_TYPES.WEIGHT_BASED : DATASET_TYPES.DAYS_ON_FEED;
    const isActiveDataset = activeDataset === datasetType;

    const firstColumnLabel = isWeightBased
      ? <FormattedMessage id="general.averageWeight" />
      : <FormattedMessage id="general.daysOnFeed" />;

    const columns = [
      {
        label: firstColumnLabel,
        renderer: (column) => this.renderFirstColumn(column, isWeightBased),
        flex: isMobile ? '0 0 140px' : '1 0 150px',
        fixed: true,
        className: 'setpoint-table-cell'
      },
      ...this.getFarmTypesColumns(isWeightBased),
    ];

    const transparentBtnStyle = { fontWeight: 400 };

    return (
      <Panel key={datasetType}>
        <Panel.Heading title={(
          <FormattedMessage
            id={isWeightBased ? 'component.setPoints.weightBasedSetpoints' : 'component.setPoints.daysOnFeedSetpoints'}
          />
        )}
        >
          <div className="setpoints-controls">
            {!isEditMode && !!safeData.length && (
              <TransparentBtn style={transparentBtnStyle} onClick={() => this.switchEditMode(datasetType)}>
                <FormattedMessage id="general.editTable" />
              </TransparentBtn>
            )}
            {isEditMode && isActiveDataset && (
              <div className="edit-buttons">
                <Button default small onClick={this.resetToDefault} className="reset-btn">
                  <FormattedMessage id="general.button.resetToDefault" />
                </Button>
                <Button primary small onClick={this.saveChanges} className="save-btn">
                  <FormattedMessage id="general.button.saveChanges" />
                </Button>
              </div>
            )}
          </div>
        </Panel.Heading>
        <Panel.Body noPadding>
          <Preloader isActive={loading} />

          <DataTable
            data={safeData}
            columns={columns}
            scrollable
            hasDesktopViewOnly
            hasScrollHint={isMobile}
          />

          <NothingBox display={!safeData.length && !loading}>
            <FormattedMessage tagName="h1" id="component.nothingBox.noSetPoints" />
          </NothingBox>
        </Panel.Body>
      </Panel>
    );
  };

  render() {
    const { activeDataset } = this.state;

    return (
      <div className="SetPoints">
        {/* Dataset Selection Tabs */}
        <section className="dataset-tabs">
          <Panel>
            <Panel.Heading title={<FormattedMessage id="general.tempSetpoints" />}>
              <div className="dataset-controls">
                <Button
                  light
                  small
                  onClick={() => this.switchDataset(DATASET_TYPES.WEIGHT_BASED)}
                  className={activeDataset === DATASET_TYPES.WEIGHT_BASED ? 'active' : ''}
                >
                  <FormattedMessage id="component.setPoints.weightBased" />
                </Button>
                <Button
                  light
                  small
                  onClick={() => this.switchDataset(DATASET_TYPES.DAYS_ON_FEED)}
                  className={activeDataset === DATASET_TYPES.DAYS_ON_FEED ? 'active' : ''}
                >
                  <FormattedMessage id="component.setPoints.daysOnFeedBased" />
                </Button>
              </div>
            </Panel.Heading>
          </Panel>
        </section>

        {/* Active Dataset Table */}
        <section className="temperature-table">
          {activeDataset === DATASET_TYPES.WEIGHT_BASED
            ? this.renderDatasetTable(true)
            : this.renderDatasetTable(false)
          }
        </section>
      </div>
    );
  }
}

SetPoints.propTypes = {
  temperatureSystem: T.string.isRequired,
  measurementSystem: T.string.isRequired,
  temperatureSetpointSource: T.string,
};

export default connect(
  (state) => ({
    temperatureSystem: state.auth.user.current_company.temperature_system,
    measurementSystem: state.auth.user.current_company.measurement_system,
    temperatureSetpointSource: state.auth.user.current_company.temperature_setpoint_source,
  })
)(SetPoints);
